import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { getEventDashboard, qrPngUrl } from '../api/owner'
import type { ActivityEntry, ChallengeProgress, RecentUpload } from '../api/ownerTypes'
import { formatDuration, formatEventDate, formatTime, plural } from '../lib/format'
import { GuestAvatar } from '../components/Monogram'
import { Spinner } from '../components/icons'
import { useEvent } from './eventContext'
import { QrCode } from './components/QrCode'
import { StatusBadge } from './components/StatusBadge'
import { readable } from './format'

/** De rijen in de "Beheren"-lijst op mobiel (ronde 20F en 20G). */
const manageRows = [
  { to: 'info', icon: '✎', label: 'Eventinfo', detail: 'Namen, datum, locatie en foto’s' },
  { to: 'gasten', icon: '☺', label: 'Gasten', detail: null },
  { to: 'qr', icon: '◫', label: 'QR-code', detail: 'Bekijken en downloaden' },
  { to: 'challenges', icon: '◎', label: 'Challenges', detail: null },
  { to: 'instellingen', icon: '⚙', label: 'Instellingen', detail: 'Privacy en kleur' },
]

/**
 * Ronde 20E, 20F, 20G en 20R — het overzicht van een event.
 *
 * Op mobiel één doorlopende pagina: beeld, titel, de knop naar de galerij, de
 * laatste uploads, wie er net iets toevoegde, de challenges, de QR en de
 * beheerlijst. Op desktop staan dezelfde blokken in twee kolommen naast de
 * zijnavigatie.
 */
export function EventDashboardPage() {
  const { subject } = useEvent()
  const navigate = useNavigate()

  const dashboard = useQuery({
    queryKey: ['owner', 'dashboard', subject.id],
    queryFn: () => getEventDashboard(subject.id),
  })

  const cover = subject.photos.at(0)

  // "Vanavond" en "gasten uploaden op dit moment" hingen aan de publicatiestatus,
  // en die gaat nooit meer terug: een feest van vorig jaar stond hier dus nog
  // altijd aan te moedigen dat de gasten aan het uploaden waren. Het hoort te
  // hangen aan of het feest vandáág is — en of er iemand bij kan.
  const vanavond = !subject.isPrivate && isToday(subject.eventDateUtc)

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-10">
      {/* --- Beeld en kop, alleen mobiel (ronde 20E) ------------------- */}
      <div className="placeholder-warm relative h-38 shrink-0 lg:hidden">
        {cover && <img src={cover.url} alt="" className="h-full w-full object-cover" />}

        <button
          type="button"
          aria-label="Terug naar mijn events"
          onClick={() => navigate('/beheer')}
          className="bg-shell/90 text-ink-muted absolute top-5 left-4.5 flex size-9.5 items-center justify-center rounded-full text-base"
        >
          ←
        </button>

        {/* Kunnen gasten erbij. Dat is op dit scherm wél iets wat je wil zien:
            het verandert, en het is de enige knop met gevolgen voor je gasten. */}
        <div className="absolute top-5 right-4.5">
          <StatusBadge isPrivate={subject.isPrivate} onSurface />
        </div>
      </div>

      <div className="px-5.5 pt-4 lg:hidden">
        <h1 className="font-display text-[32px] leading-[1.05]">{subject.title}</h1>
        <p className="text-ink-soft mt-1.25 text-[13px]">
          {subtitle(subject.isPrivate, subject.eventDateUtc)}
        </p>
      </div>

      {/* --- Kop, alleen desktop (ronde 20R) --------------------------- */}
      <div className="hidden items-end justify-between px-12 pt-8.5 lg:flex">
        <div>
          <h1 className="font-display text-[40px] leading-[1.05]">
            {vanavond ? 'Vanavond' : subject.title}
          </h1>
          <p className="text-ink-soft mt-2 text-[15px]">
            {vanavond
              ? 'Het feest loopt. Gasten uploaden op dit moment.'
              : subtitle(subject.isPrivate, subject.eventDateUtc)}
          </p>
        </div>

        <a
          href={subject.guestUrl}
          target="_blank"
          rel="noreferrer"
          className="bg-accent text-accent-ink shadow-accent flex h-12.5 items-center rounded-full px-6 text-[15px] font-semibold transition hover:brightness-95"
        >
          Naar de galerij
        </a>
      </div>

      <div className="px-5.5 pt-4 lg:hidden">
        <a
          href={subject.guestUrl}
          target="_blank"
          rel="noreferrer"
          className="bg-accent text-accent-ink shadow-accent flex h-13 items-center justify-center rounded-full text-base font-semibold transition hover:brightness-95"
        >
          Naar de galerij
        </a>
      </div>

      {/*
        Zo groot als het is. Privé betekent dat er van je gasten niets meer kan
        binnenkomen én dat niemand nog kan kijken; dat is te ingrijpend voor
        alleen een pilletje in een hoek, zeker omdat het een stand is die je
        aanzet en dan weken laat staan. Met de weg terug erbij, want die zoek je
        anders in het menu.
      */}
      {subject.isPrivate && (
        <div className="px-5.5 pt-4.5 lg:px-12 lg:pt-6">
          <div className="rounded-card border-ink/25 bg-notice border-[1.5px] p-4 lg:max-w-[520px] lg:p-5">
            <p className="flex items-center gap-2 text-[15px] font-semibold lg:text-base">
              <span aria-hidden="true">🔒</span>
              Dit event staat privé
            </p>
            <p className="text-ink-soft mt-1.5 text-[13px] leading-[1.5] lg:text-sm">
              Niemand kan de galerij bekijken of er iets aan toevoegen. De QR-code werkt niet meer.
              Jij kan er als eigenaar altijd bij.
            </p>

            <Link
              to="instellingen"
              className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink mt-3.5 inline-flex h-10.5 items-center rounded-full border px-4.5 text-sm transition-colors"
            >
              Weer openzetten
            </Link>
          </div>
        </div>
      )}

      {dashboard.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner className="text-accent size-6" />
        </div>
      ) : (
        <div className="lg:flex lg:gap-8 lg:px-12 lg:pt-7">
          <div className="min-w-0 lg:flex-1">
            <RecentUploads
              uploads={dashboard.data?.recentUploads ?? []}
              guestUrl={subject.guestUrl}
            />
            <Activity entries={dashboard.data?.activity ?? []} />
          </div>

          <div className="lg:w-80 lg:shrink-0">
            <QrPanel
              eventId={subject.id}
              displayUrl={subject.displayUrl}
              onOpen={() => navigate('qr')}
            />
            <Challenges
              challenges={dashboard.data?.challenges ?? []}
              guestCount={dashboard.data?.guestCount ?? 0}
            />
          </div>
        </div>
      )}

      {/* --- Beheren, alleen mobiel (ronde 20G) ------------------------ */}
      <div className="px-5.5 pt-5.5 lg:hidden">
        <p className="text-ink-faint mb-3 text-[11px] tracking-[0.18em] uppercase">Beheren</p>

        <div className="rounded-card border-line-card bg-surface overflow-hidden border">
          {manageRows.map((row, index) => (
            <Link
              key={row.to}
              to={row.to}
              className={
                'flex items-center gap-3.25 px-3.75 py-3.75 ' +
                (index < manageRows.length - 1 ? 'border-line-menu border-b' : '')
              }
            >
              <span className="bg-tint text-accent flex size-9.5 shrink-0 items-center justify-center rounded-[11px] text-[15px]">
                {row.icon}
              </span>
              <span className="flex-1">
                <span className="block text-[15px] font-medium">{row.label}</span>
                <span className="text-ink-faint mt-0.5 block text-[13px]">
                  {row.detail ?? detailFor(row.to, subject.guestCount, subject.challengeCount)}
                </span>
              </span>
              <span aria-hidden="true" className="text-ink-ghost text-lg">
                ›
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* --- Downloaden en toegang (ronde 20F) ------------------------- */}
      <div className="px-5.5 pt-4.5 lg:px-12 lg:pt-8 lg:pb-4">
        <div className="rounded-card border-line-card bg-surface overflow-hidden border lg:max-w-[520px]">
          <div className="border-line-menu flex items-center gap-3.25 border-b px-3.75 py-3.75 opacity-55">
            <span className="bg-tint text-accent flex size-9 shrink-0 items-center justify-center rounded-[11px] text-[15px]">
              ⤓
            </span>
            <span className="flex-1">
              <span className="block text-[15px]">Alles downloaden</span>
              <span className="text-ink-faint mt-0.5 block text-[13px]">
                {plural(subject.mediaCount, 'foto', 'foto’s')} als zip — komt nog
              </span>
            </span>
          </div>

          <Link to="instellingen" className="flex items-center gap-3.25 px-3.75 py-3.75">
            <span className="bg-tint text-accent flex size-9 shrink-0 items-center justify-center rounded-[11px] text-[15px]">
              ◷
            </span>
            <span className="flex-1">
              <span className="block text-[15px]">
                {subject.isPrivate ? 'Galerij weer openzetten' : 'Galerij op privé zetten'}
              </span>
              <span className="text-ink-faint mt-0.5 block text-[13px]">
                {subject.isPrivate ? 'Nu kan alleen jij erbij' : 'Dan kan alleen jij er nog bij'}
              </span>
            </span>
            <span aria-hidden="true" className="text-ink-ghost text-lg">
              ›
            </span>
          </Link>
        </div>
      </div>
    </div>
  )
}

function RecentUploads({ uploads, guestUrl }: { uploads: RecentUpload[]; guestUrl: string }) {
  if (uploads.length === 0) {
    return (
      <div className="px-5.5 pt-6 lg:px-0 lg:pt-0">
        <p className="text-ink-faint text-[11px] tracking-[0.18em] uppercase lg:text-xs">
          Laatste uploads
        </p>
        <p className="text-ink-soft mt-3 text-sm">
          Nog niemand heeft iets gedeeld. Zodra dat gebeurt zie je het hier.
        </p>
      </div>
    )
  }

  return (
    <div className="px-5.5 pt-6 lg:px-0 lg:pt-0">
      <div className="flex items-baseline justify-between">
        <p className="text-ink-faint text-[11px] tracking-[0.18em] uppercase lg:text-xs">
          Laatste uploads
        </p>
        <a
          href={guestUrl}
          target="_blank"
          rel="noreferrer"
          className="text-accent text-[13px] lg:text-sm"
        >
          Alles bekijken
        </a>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1.5 lg:mt-3.5 lg:gap-2">
        {uploads.map((upload) => (
          <div
            key={upload.id}
            className="placeholder-warm relative aspect-square overflow-hidden rounded-[5px] lg:rounded-md"
          >
            <img src={upload.webUrl} alt="" loading="lazy" className="h-full w-full object-cover" />

            {upload.kind === 'Video' && (
              <span className="bg-shell/85 text-ink-muted absolute bottom-1.25 left-1.25 rounded-[2px] px-1.25 py-px text-[10px] lg:bottom-2 lg:left-2 lg:text-xs">
                {formatDuration(upload.durationSeconds) ?? 'video'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function Activity({ entries }: { entries: ActivityEntry[] }) {
  if (entries.length === 0) {
    return null
  }

  return (
    <div className="px-5.5 pt-6 lg:px-0 lg:pt-7.5">
      <p className="text-ink-faint text-[11px] tracking-[0.18em] uppercase lg:text-xs">
        Net toegevoegd
      </p>

      <div className="rounded-notice border-line-card bg-surface lg:rounded-card mt-3 overflow-hidden border lg:mt-3.5">
        {entries.map((entry, index) => (
          <div
            key={`${entry.guestId}-${entry.atUtc}`}
            className={
              'flex items-center gap-3 px-3.5 py-3.25 lg:gap-3.5 lg:px-5 lg:py-3.75 ' +
              (index > 0 ? 'border-line-menu border-t' : '')
            }
          >
            <GuestAvatar displayName={entry.guestName} size={34} />
            <p className="flex-1 text-sm leading-[1.35] lg:text-[15px]">{sentence(entry)}</p>
            <p className="text-ink-faint text-[13px] lg:text-sm">{formatTime(entry.atUtc)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function QrPanel({
  eventId,
  displayUrl,
  onOpen,
}: {
  eventId: string
  displayUrl: string
  onOpen: () => void
}) {
  return (
    <div className="px-5.5 pt-6 lg:px-0 lg:pt-0">
      {/* Op mobiel een brede rij (ronde 20F), op desktop een paneel (20R). */}
      <div className="rounded-notice border-line-card bg-surface lg:rounded-card flex items-center gap-4 border p-4 lg:flex-col lg:gap-0 lg:p-5.5 lg:text-center">
        <div className="lg:hidden">
          <QrCode src={qrPngUrl(eventId)} size={72} className="border-line-card border" />
        </div>

        <p className="text-ink-faint hidden text-xs tracking-[0.18em] uppercase lg:block">
          QR-code
        </p>
        <div className="mt-4 hidden lg:block">
          <QrCode src={qrPngUrl(eventId)} size={132} className="border-line-card border" />
        </div>

        <div className="flex-1 lg:w-full lg:flex-none">
          <p className="text-[15px] font-semibold lg:hidden">QR-code</p>
          <p className="text-ink-soft lg:text-ink-muted mt-1 font-mono text-xs lg:mt-3.5 lg:text-[12px]">
            {readable(displayUrl)}
          </p>
          <button
            type="button"
            onClick={onOpen}
            className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink mt-2.5 inline-flex h-9 items-center rounded-full border px-4 text-sm transition-colors lg:mt-4 lg:flex lg:h-11 lg:w-full lg:justify-center"
          >
            Downloaden
          </button>
        </div>
      </div>
    </div>
  )
}

function Challenges({
  challenges,
  guestCount,
}: {
  challenges: ChallengeProgress[]
  guestCount: number
}) {
  if (challenges.length === 0) {
    return null
  }

  return (
    <div className="px-5.5 pt-5.5 lg:px-0 lg:pt-4">
      <div className="rounded-notice border-line-card bg-surface lg:rounded-card border p-4 lg:p-5">
        <p className="text-ink-faint mb-3.5 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
          Challenges
        </p>

        <div className="flex flex-col gap-3.5 lg:gap-3">
          {challenges.slice(0, 3).map((challenge) => (
            <div key={challenge.id}>
              <div className="flex justify-between text-sm">
                <span>{challenge.title}</span>
                <span className="text-ink-faint">
                  {plural(challenge.guestCount, 'gast', 'gasten')}
                </span>
              </div>
              <div className="bg-line-menu mt-1.75 h-1 overflow-hidden rounded-full">
                <div
                  className="bg-accent h-full"
                  style={{
                    width: `${guestCount === 0 ? 0 : Math.round((challenge.guestCount / guestCount) * 100)}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** "Jasper & Nele voegden 4 foto's toe" of "Tante Marleen filmde ...". */
function sentence(entry: ActivityEntry): string {
  if (entry.kind === 'Video') {
    return entry.count === 1
      ? `${entry.guestName} deelde een video`
      : `${entry.guestName} deelde ${entry.count} video’s`
  }

  return entry.count === 1
    ? `${entry.guestName} voegde een foto toe`
    : `${entry.guestName} voegde ${entry.count} foto’s toe`
}

/**
 * De regel onder de titel: kunnen gasten erbij, en wanneer is het feest.
 *
 * Er stond "Het feest loopt" zodra er gepubliceerd was, en dat bleef staan tot
 * in de eeuwigheid. Wat er nu staat gaat over toegang, en dat is wel altijd waar.
 */
function subtitle(isPrivate: boolean, eventDateUtc: string | null): string {
  const date = eventDateUtc ? formatEventDate(eventDateUtc) : 'Nog geen datum'

  return isPrivate ? `Galerij op privé · ${date}` : `Galerij staat open · ${date}`
}

/** Of dit de dag zelf is, in de tijdzone van wie ernaar kijkt. */
function isToday(eventDateUtc: string | null): boolean {
  if (!eventDateUtc) {
    return false
  }

  const day = new Date(eventDateUtc)
  const now = new Date()

  return (
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate()
  )
}

function detailFor(to: string, guestCount: number, challengeCount: number): string {
  if (to === 'gasten') {
    return `${guestCount} geregistreerd`
  }

  return to === 'challenges' ? `${challengeCount} actief` : ''
}
